/**
 * Générateur — Donner à la lettre le statut d'inconnue.
 * Attendu ID : 941 | Niveau : 5ème | Domaine : Calcul littéral (cluster 8, O43)
 *
 * Source : doc prof « TANOED EXERCISEUR nombre et calcul », thème Calcul
 * littéral, cluster 8 (Créé n°28 « Du mot à la lettre » + Ex.1E/2E réaffectés,
 * double étiquette O43/O44 PORTÉE ICI — gen_942 porte la modélisation de
 * situations, Créé n°29a).
 *
 * Plan de cartes (validé 2026-07-12) :
 *
 *   Carte « penser » — Créé n°28 : la progression verbalisation → lettre du
 *   cadrage. texte_trou_drag (« Le nombre cherché vérifie {{le nombre}} +
 *   7 = 15. On l'appelle x : {{x}} + 7 = 15 ») + qcm_unique (« quelle
 *   équation traduit le problème ? » — distracteurs : opération lue produit,
 *   sens inversé, nombres échangés) + texte_trou_saisie (valeur de x +
 *   vérification). Familles : ajouter b, retirer b, multiplier par a
 *   (solution entière par construction : c = a·x).
 *
 *   Carte « trous » — Ex.1E/2E : le pont « opération à trou → équation ».
 *   texte_trou_saisie (3 opérations à trous, dont le cas NÉGATIF du doc
 *   18 + … = 8 → -10, signe ASCII + consigne) + relier (opération à trou ↔
 *   équation — couples (coefficient, résultat) tous distincts par
 *   construction pour interdire les croisements).
 */

import {
    randomChoice, randInt,
    makeQcm, makeTexteTrou, makeRelier, filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 941;

const L_PENSER = 'Je pense à un nombre… (du mot à la lettre)';
const L_TROUS = 'Des opérations à trous aux équations';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const MOINS = '−';   // U+2212 (affichage uniquement — en SAISIE : ASCII « - »)

// ═════════ Carte 1 — Je pense à un nombre (Créé n°28) ═════════

/**
 * Une devinette à solution par construction. `gabarit(mot)` rend l'équation
 * verbalisée avec `mot` à la place de l'inconnue (« le nombre », « x »…) ;
 * `verifTrou` est la vérification avec son trou déjà posé.
 */
const tirerRiddle = () => {
    const famille = randomChoice(['plus', 'moins', 'fois']);
    if (famille === 'plus') {
        const x = randInt(2, 30);
        const b = randInt(2, 20);
        const c = x + b;
        return {
            famille, x, b, c,
            riddle: `En lui ajoutant ${b}, j'obtiens ${c}.`,
            eq: `x + ${b} = ${c}`,
            gabarit: (mot) => `${mot} + ${b} = ${c}`,
            eqFausses: [`${b}x = ${c}`, `x ${MOINS} ${b} = ${c}`, `x + ${c} = ${b}`],
            verifTrou: `{{${x}}} + ${b} = ${c}`,
        };
    }
    if (famille === 'moins') {
        const b = randInt(2, 20);
        const c = randInt(2, 30);
        const x = b + c;
        return {
            famille, x, b, c,
            riddle: `En lui retirant ${b}, j'obtiens ${c}.`,
            eq: `x ${MOINS} ${b} = ${c}`,
            gabarit: (mot) => `${mot} ${MOINS} ${b} = ${c}`,
            eqFausses: [`x + ${b} = ${c}`, `${b}x = ${c}`, `${b} ${MOINS} x = ${c}`],
            verifTrou: `{{${x}}} ${MOINS} ${b} = ${c}`,
        };
    }
    const a = randInt(2, 9);
    const x = randInt(2, 12);
    const c = a * x;
    return {
        famille, x, a, c,
        riddle: `En le multipliant par ${a}, j'obtiens ${c}.`,
        eq: `${a}x = ${c}`,
        gabarit: (mot) => `${a} × ${mot} = ${c}`,
        eqFausses: [`x + ${a} = ${c}`, `x ÷ ${a} = ${c}`, `${c}x = ${a}`],
        verifTrou: `${a} × {{${x}}} = ${c}`,
    };
};

const genPenserDrag = () => {
    const t = tirerRiddle();
    const texte = `<p><b>Je pense à un nombre. ${t.riddle}</b> Complète le raisonnement.</p>`;
    const phrase = `Le nombre cherché vérifie : ${t.gabarit('{{le nombre}}')}. `
        + `On décide d'appeler x le nombre cherché : l'équation s'écrit ${t.gabarit('{{x}}')}.`;
    const distracteurs = filtrerDistracteurs(phrase,
        ['la solution', 'y', 'le résultat', String(t.x)]).slice(0, 3);
    const payload = makeTexteTrou(phrase, texte,
        { ...opts(L_PENSER), mode: 'drag', distracteurs });
    payload._meta.params = { famille: t.famille, x: t.x };
    return payload;
};

const genPenserQcm = () => {
    const t = tirerRiddle();
    const texte = `<p><b>Je pense à un nombre, que l'on appelle x. ${t.riddle}</b> `
        + 'Quelle équation traduit le problème ?</p>';
    const distincts = [...new Set(t.eqFausses)].filter(e => e !== t.eq).slice(0, 3);
    const payload = makeQcm([t.eq, ...distincts], t.eq, texte, opts(L_PENSER));
    payload._meta.params = { famille: t.famille, x: t.x };
    return payload;
};

const genPenserTrous = () => {
    const t = tirerRiddle();
    const texte = `<p><b>Je pense à un nombre x. ${t.riddle}</b> `
        + `L'équation est ${t.eq}. Trouve x, puis vérifie.</p>`;
    const phrase = `x = {{${t.x}}}. Vérification : ${t.verifTrou} ✔`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_PENSER), mode: 'saisie' });
    payload._meta.params = { famille: t.famille, x: t.x };
    return payload;
};

// ═════════ Carte 2 — Des opérations à trous aux équations (Ex.1E/2E) ═════════

/**
 * Un item d'opération à trou : { affiche (avec …), trou, equation }.
 * Couples (coefficient/terme, résultat) garantis distincts entre items d'un
 * même tirage (le tirage écarte les doublons de rendu).
 */
// `leurre` = résultat de la MAUVAISE opération inverse (piège type C5 : le format
// drag l'offre comme distracteur à côté de la bonne valeur). Toujours distinct du
// trou (a ≥ 2, b ≥ 2) et pédagogiquement parlant (add/sous, signe, ×/÷).
const tirerItemTrou = () => {
    const famille = randomChoice(['fois', 'plusPositif', 'plusNegatif', 'moins']);
    if (famille === 'fois') {
        const a = randInt(2, 9);
        const x = randInt(2, 12);
        const c = a * x;
        return {
            famille,
            affiche: `${a} × … = ${c}`,
            trou: String(x),
            equation: `${a}x = ${c}`,
            leurre: String(a * c),        // × au lieu de ÷
        };
    }
    if (famille === 'plusPositif') {
        const b = randInt(2, 20);
        const x = randInt(2, 20);
        const c = b + x;
        return {
            famille,
            affiche: `${b} + … = ${c}`,
            trou: String(x),
            equation: `${b} + x = ${c}`,
            leurre: String(c + b),        // + au lieu de −
        };
    }
    if (famille === 'plusNegatif') {
        // le cas du doc : 18 + … = 8 → -10
        const b = randInt(10, 25);
        const c = randInt(2, b - 2);
        return {
            famille,
            affiche: `${b} + … = ${c}`,
            trou: String(c - b),          // négatif, ASCII
            equation: `${b} + x = ${c}`,
            leurre: String(b - c),        // signe oublié (10 au lieu de -10)
        };
    }
    // « moins » : on impose c > b pour que le leurre (c − b) reste positif et plausible
    const b = randInt(2, 12);
    const c = randInt(b + 1, 20);
    return {
        famille,
        affiche: `… ${MOINS} ${b} = ${c}`,
        trou: String(b + c),
        equation: `x ${MOINS} ${b} = ${c}`,
        leurre: String(c - b),            // − au lieu de +
    };
};

/** n items de familles/rendus tous distincts (re-tirage borné + repli). */
const tirerItemsTrous = (n) => {
    const items = [];
    const affiches = new Set();
    const famillesVues = new Set();
    for (let essai = 0; essai < 60 && items.length < n; essai++) {
        const it = tirerItemTrou();
        if (affiches.has(it.affiche)) continue;
        if (famillesVues.has(it.famille) && essai < 30) continue;   // privilégier la diversité
        affiches.add(it.affiche);
        famillesVues.add(it.famille);
        items.push(it);
    }
    // repli déterministe (vérifié à la main), ajouté seulement si inédit
    const REPLI = [
        { famille: 'fois', affiche: '4 × … = 8', trou: '2', equation: '4x = 8' },
        { famille: 'plusPositif', affiche: '5 + … = 15', trou: '10', equation: '5 + x = 15' },
        { famille: 'plusNegatif', affiche: '18 + … = 8', trou: '-10', equation: '18 + x = 8' },
        { famille: 'moins', affiche: `… ${MOINS} 3 = 7`, trou: '10', equation: `x ${MOINS} 3 = 7` },
    ];
    for (let i = 0; i < REPLI.length && items.length < n; i++) {
        if (!affiches.has(REPLI[i].affiche)) { affiches.add(REPLI[i].affiche); items.push(REPLI[i]); }
    }
    return items;
};

// Le pont « trou → x → valeur » sur UNE opération (attendu 941 : la lettre prend
// le statut d'inconnue). Drag : faire glisser l'étiquette « x » à la place du
// nombre manquant incarne le geste, et le leurre matérialise l'erreur type (C5).
const genTrousDrag = () => {
    const it = tirerItemTrou();
    const eqAvecX = it.affiche.replace('…', '{{x}}');
    const negatif = it.trou.startsWith('-');
    const texte = `<p><b>Dans l'opération à trou « ${it.affiche} », le nombre `
        + 'manquant est inconnu : on l\'appelle x.</b> Fais glisser x à sa place '
        + 'dans l\'équation, puis donne sa valeur.'
        + (negatif ? ' (Le nombre cherché peut être négatif, ex. : -10.)' : '')
        + '</p>';
    const phrase = `L'opération « ${it.affiche} » devient l'équation ${eqAvecX}, `
        + `où x est le nombre cherché. On trouve x = {{${it.trou}}}.`;
    const distracteurs = filtrerDistracteurs(phrase, [it.leurre]).slice(0, 2);
    const payload = makeTexteTrou(phrase, texte,
        { ...opts(L_TROUS), mode: 'drag', distracteurs });
    payload._meta.params = { famille: it.famille, affiche: it.affiche };
    return payload;
};

const genTrousRelier = () => {
    const items = tirerItemsTrous(4);
    const pairs = items.map(it => ({ source: it.affiche, target: it.equation }));
    const payload = makeRelier(pairs,
        '<p><b>Le nombre manquant devient l\'inconnue x.</b> Relie chaque opération '
        + 'à trou à l\'équation qui la traduit.</p>',
        opts(L_TROUS));
    payload._meta.params = { items: items.map(i => i.affiche) };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    penser_texte_trou_drag: genPenserDrag,
    penser_qcm_unique: genPenserQcm,
    penser_texte_trou_saisie: genPenserTrous,
    trous_texte_trou_drag: genTrousDrag,
    trous_relier: genTrousRelier,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', relier: 'Relier',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'penser', label: L_PENSER, formatsList: buildFormats('penser') },
    { id: 'trous', label: L_TROUS, formatsList: buildFormats('trous') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
